import { FC } from 'react'
import { useHistory } from 'react-router-dom'
import { useMutation } from '@apollo/client'
import { Button } from 'react-bootstrap'

import { useAppDispatch, useAppSelector } from 'hooks/store'
import useLoadingDelay from 'hooks/useLoadingDelay'
import { loadingSpinnerPortal } from 'components/atoms/LoadingSpinner'
import {
  getActiveTabCourseOrderItem,
  getEmptyCourseOrderItem,
  getActiveDiscInstUrlInfo,
  setAcceptedDisclosureAgreementTrue
} from 'reducers/activecourse/activecourseSlice'
import {
  UPDATE_ORDER_ACCEPTED_DISCLOSURE_FLAG,
  UpdateOrderAcceptedDisclosureAgreementFlagVariables,
  UpdateOrderAcceptedDisclosureAgreementFlagResponse
} from 'share/mutations/courses'
import GoBackHeader from 'components/organisms/GoBackHeader'
import {
  shouldNavigateToCourseInstructions,
  mainCoursePagePath
} from 'share/utils/courseUtils'
import { CourseType } from 'share/domain/course'
import { TwoBtnsContainer } from 'components/atoms/Layout'

import {
  CourseDisclosureContainer,
  CourseDisclosureContent,
  CourseDisclosureTitle
} from './styles'

const CourseDisclosureAgreement: FC = () => {
  const history = useHistory()
  const dispatch = useAppDispatch()

  const activeCase = useAppSelector((state) => state.activeCase)
  const activeCourse = useAppSelector((state) => state.activeCourse)
  const course = useAppSelector(getActiveTabCourseOrderItem)
  const discInstUrlInfo = useAppSelector(getActiveDiscInstUrlInfo)

  const [updateAcceptedDisclosureFlag, { loading }] = useMutation<
    UpdateOrderAcceptedDisclosureAgreementFlagResponse,
    UpdateOrderAcceptedDisclosureAgreementFlagVariables
  >(UPDATE_ORDER_ACCEPTED_DISCLOSURE_FLAG)

  const loadingDelay = useLoadingDelay(loading)

  async function updateAcceptedDisclosureFlagHelper(): Promise<boolean> {
    let result = false
    try {
      const input = {
        flag: true,
        courseType: course ? course.courseType : CourseType.CreditCounseling,
        caseId:
          activeCase.matter && activeCase.matter.id ? activeCase?.matter.id : 0,
        disclosureCourseOrderItemId: discInstUrlInfo
          ? discInstUrlInfo.courseOrderItemId
          : 0,
        disclosureVersion: discInstUrlInfo
          ? discInstUrlInfo.disclosureVersion
          : 0
      }

      const updateResult = await updateAcceptedDisclosureFlag({
        variables: { request: input }
      })
      if (
        updateResult.data?.orders.updateOrderAcceptedDisclosureAgreementFlag
          .success
      ) {
        result = true

        dispatch(setAcceptedDisclosureAgreementTrue(activeCourse.activeTab))
      }
    } catch (e) {
      result = false
    }

    return result
  }

  async function agreeAndConinueClick() {
    let path: string | null = null

    await updateAcceptedDisclosureFlagHelper()
    if (
      (path = shouldNavigateToCourseInstructions(
        course ? course : getEmptyCourseOrderItem()
      ))
    ) {
      history.push(path)
    } else {
      history.push(mainCoursePagePath())
    }
  }

  async function handleBackButtonClick() {
    history.push({
      pathname: '/dashboard/courses'
    })
  }

  return (
    <>
      <GoBackHeader text={course?.providerName} />
      {loadingDelay && loadingSpinnerPortal()}
      <CourseDisclosureContainer data-testid='course-disclosure-agreement-page'>
        <CourseDisclosureTitle data-testid='course-disclosure-agreement-title'>
          Disclosure Agreement
        </CourseDisclosureTitle>
        <CourseDisclosureContent
          data-testid='course-disclosure-agreement-content'
          dangerouslySetInnerHTML={{
            __html: discInstUrlInfo?.disclosureContent || ''
          }}
        />
        <TwoBtnsContainer>
          <Button
            variant='outline-secondary'
            onClick={handleBackButtonClick}
            data-testid='course-disclosure-agreement-back-button'
          >
            Back
          </Button>
          <Button
            variant='primary'
            onClick={agreeAndConinueClick}
            data-testid='course-disclosure-agreement-continue-button'
          >
            Agree &amp; Continue
          </Button>
        </TwoBtnsContainer>
      </CourseDisclosureContainer>
    </>
  )
}

export default CourseDisclosureAgreement
